import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Box, IconButton } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import GenericDraggableTableView from "../../../../../Features/Table/GenericDraggableTable";
import { getColumnsConfig } from "../../../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import { PrimaryTooltip } from "../../../../../Components/StyledComponent";
import { getPagesMetaData } from "../../../../../Api/Api";
import {
    getBatteryComponents,
    getBatteryServiceRecords,
    serviceRecordsListAction,
    getServiceRecordArchivePath,
    getServiceRecordSearchSuggestions,
} from "../../../../../Api/ServiceRecordApi";
import { transformTwinHierarchyData } from "../../../../../Helper/DigitalTwin/DigitalTwinFunctions";
import { extractStringsWithPacks } from "../../../../../Helper/DigitalTwin/BatteryComponentsHelper";
import { useToast } from "../../../../../Hooks/useToast";
import {
    LOGS_COLUMNS_CONFIG,
    LOGS_COLUMN_ORDERING,
    LOGS_FILTER_DEFS,
    LOGS_MAX_COLUMNS,
    SORT_KEY_MAP,
    getDefaultSelectedColumns,
} from "./Data/ReplacementLogsColumns";
import {
    groupServiceRecordRows,
    getExpandableRowKeys,
    mapSelectedFiltersToParams,
} from "./Data/replacementLogsHelper";
import CustomIcon from "../../../../../Components/CustomIcon";
import { useVehicleInfo, useBatteryInfo } from "../../../../../Pages/DigitalTwinV3/DigitalTwinContext";
import { encryptTheParams } from "../../../../../Helper/QueryParams/EncryptDecrypt";
import {
    getAvailableEmailDomains,
    DEFAULT_EMAIL_DOMAINS,
} from "../../../../../Helper/Organization/EmailDomains";

const PAGE_SIZE = 20;
// Two identifiers, picked from the search box's own field dropdown. The chosen key
// travels to the API as `searchBy`, so a request id is matched against the request
// and a serial against its packs, rather than both being tried at once.
const SEARCH_FIELDS = [
    { key: "requestID", title: "Request ID" },
    { key: "serialNumber", title: "Serial number" },
];
const DEFAULT_SEARCH_FIELD = SEARCH_FIELDS[0].key;

// Expand control on a request row. Returns null for a pack row and for a request
// with no packs, so only rows that actually open get a control.
const LogsExpandIcon = ({ expanded, onExpand, record }) => {
    if (!record?.children?.length) return null;
    return (
        <IconButton
            disableRipple
            disableTouchRipple
            disableFocusRipple
            data-testid={`log-expand-${record.id}`}
            aria-label={expanded ? "Collapse packs" : "Expand packs"}
            sx={{
                fontSize: "16px",
                paddingLeft: 0,
                backgroundColor: "transparent !important",
                "&:hover": { backgroundColor: "transparent !important" },
            }}
            onClick={(e) => onExpand(record, e)}
        >
            <CustomIcon name="primary-arrow-icon" rotate={expanded ? 90 : 0} />
        </IconButton>
    );
};

// Build the master-detail filter object (shape TableFilters expects) from the
// org-meta (or fallback) filter definitions, injecting the per-battery option
// values by key. dateCalender/date filters need no values.
const buildFilters = (filterDefs, { packValues, stringValues, reasonValues, recorderValues }) => {
    const valueByKey = {
        pack: packValues,
        stringNo: stringValues,
        reason: reasonValues,
        recordedBy: recorderValues,
    };
    const filters = {};
    (filterDefs || []).forEach((def) => {
        filters[def.key] = {
            key: def.key,
            label: def.label,
            displayType: def.displayType,
            values: valueByKey[def.key] || def.staticData || [],
            ...(def.note ? { note: def.note } : {}),
        };
    });
    return filters;
};

export default function ReplacementLogsTable({ batteryID }) {
    const { openNotification } = useToast();
    const { t } = useTranslation(["digital_twin"]);
    const navigate = useNavigate();
    const vehicleInfo = useVehicleInfo();
    const { pageType } = useBatteryInfo();

    // Open one log full-page in a new tab (§C), passing the row context. The pack
    // and string labels are not passed: a request row covers several packs, and the
    // detail page derives its own header from the record it fetches.
    const openLogDetail = useCallback(
        (record) =>
            encryptTheParams(
                {
                    batteryID,
                    serviceRecordID: record.id,
                    vehicleNumber: vehicleInfo?.licensePlateNo || "",
                    pageType: pageType || "",
                },
                navigate,
                false,
                "/replacement-log",
                true
            ),
        [batteryID, vehicleInfo, pageType, navigate]
    );

    const [config, setConfig] = useState(null);
    const [logsTableConfig, setLogsTableConfig] = useState(null);
    const [logsFilterDefs, setLogsFilterDefs] = useState(null);
    // Domains the email composer accepts a recipient from. Resolved from
    // globalConfig["email.domains"] on the same digitalTwin pageMeta this table
    // already fetches for its columns and filters, so the allowed list is org
    // config rather than code.
    const [emailDomains, setEmailDomains] = useState(DEFAULT_EMAIL_DOMAINS);
    const [filters, setFilters] = useState({});
    const [strings, setStrings] = useState([]);
    const [recorders, setRecorders] = useState([]);

    const [tableData, setTableData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [pageNumber, setPageNumber] = useState(1);
    const [total, setTotal] = useState(0);

    const [selectedFilters, setSelectedFilters] = useState({});
    const [sorting, setSorting] = useState(null); // { sortBy, orderBy }
    const [searchTerm, setSearchTerm] = useState([]);
    const [searchField, setSearchField] = useState(DEFAULT_SEARCH_FIELD);

    const [selectedColumns, setSelectedColumns] = useState([]);
    const [columnOrder, setColumnOrder] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const lastFetchParams = useRef(null);

    // --- config + filter option sources ---
    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const [metaRes, componentsRes] = await Promise.all([
                    getPagesMetaData("digitalTwin"),
                    getBatteryComponents(batteryID),
                ]);
                if (!active) return;
                const metaInfo = metaRes?.response?.response?.metaInfo || {};
                const replacement = metaInfo?.components?.replacement || {};
                const tableCfg = metaInfo?.table?.replacementLogsTable || null;
                const filterDefs = metaInfo?.filterConfigurations?.replacementLogsTable?.filters || null;
                const tree = transformTwinHierarchyData(
                    componentsRes?.response?.BATTERY_SYSTEM || [],
                    t
                );
                setConfig(replacement);
                setLogsTableConfig(tableCfg);
                setLogsFilterDefs(filterDefs);
                setEmailDomains(getAvailableEmailDomains(metaInfo));
                setStrings(extractStringsWithPacks(tree));
                // Seed the visible-column selection from the resolved config.
                setSelectedColumns(
                    getDefaultSelectedColumns(
                        tableCfg?.columnsConfig || LOGS_COLUMNS_CONFIG,
                        tableCfg?.columnOrdering || LOGS_COLUMN_ORDERING
                    )
                );
            } catch (err) {
                console.error("Failed to load replacement logs config:", err);
                if (active) setConfig({});
            }
        })();
        return () => {
            active = false;
        };
    }, [batteryID, t]);

    useEffect(() => {
        // Filter values send NUMERIC ids (absPackNo / stringNo) that the backend
        // filters on; the label is only for display.
        const packValues = [];
        (strings || []).forEach((s) =>
            (s.packs || []).forEach((p) => packValues.push({ id: p.absPackNo, value: p.label }))
        );
        const stringValues = (strings || []).map((s) => ({ id: s.id, value: s.label }));
        const reasonValues = (config?.problemsReported || []).map((o) => ({ id: o.code, value: o.label }));
        const recorderValues = (recorders || []).map((r) => ({ id: r.userID, value: r.name }));
        setFilters(
            buildFilters(logsFilterDefs || LOGS_FILTER_DEFS, {
                packValues,
                stringValues,
                reasonValues,
                recorderValues,
            })
        );
    }, [strings, config, recorders, logsFilterDefs]);

    // --- columns (from org meta, else the local fallback) ---
    const columnsConfig = logsTableConfig?.columnsConfig || LOGS_COLUMNS_CONFIG;
    const columnOrdering = logsTableConfig?.columnOrdering || LOGS_COLUMN_ORDERING;

    const allColumns = useMemo(
        () =>
            getColumnsConfig({
                tableColumns: columnsConfig,
                tableColumnOrder: columnOrdering,
                shouldShowActionMenu: () => false,
                shouldShowColumn: () => true,
            }),
        [columnsConfig, columnOrdering]
    );

    const defaultSelected = useMemo(
        () => getDefaultSelectedColumns(columnsConfig, columnOrdering),
        [columnsConfig, columnOrdering]
    );

    const actionsColumn = useMemo(
        () => ({
            key: "actions",
            dataIndex: "actions",
            title: "Actions",
            headerLabel: "Actions",
            fixed: "right",
            width: 110,
            align: "center",
            render: (_value, record) => {
                // Actions belong to the request, so a pack row carries none:
                // viewing or downloading a pack would open the very same record
                // its parent row already opens.
                if (record._isPackRow) return null;
                return (
                    <Box sx={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                        <PrimaryTooltip title="View Log">
                            <IconButton
                                size="small"
                                data-testid={`log-view-${record.id}`}
                                onClick={() => openLogDetail(record)}
                                sx={{ color: "#494949", "&:hover": { color: "#6D72F6" } }}
                            >
                                <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </PrimaryTooltip>
                        {record._documentsCount > 0 && (
                            <PrimaryTooltip title="Download attached documents">
                                <IconButton
                                    size="small"
                                    data-testid={`log-download-${record.id}`}
                                    onClick={() =>
                                        window.open(
                                            getServiceRecordArchivePath(record.id),
                                            "_blank"
                                        )
                                    }
                                    sx={{ color: "#494949", "&:hover": { color: "#6D72F6" } }}
                                >
                                    <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </PrimaryTooltip>
                        )}
                    </Box>
                );
            },
        }),
        [openLogDetail]
    );

    const tableColumns = useMemo(() => {
        const visible = columnOrdering
            .filter((k) => selectedColumns.includes(k))
            .map((k) => allColumns.find((c) => c.dataIndex === k));
        return [...visible.filter(Boolean), actionsColumn];
    }, [allColumns, selectedColumns, actionsColumn, columnOrdering]);

    useEffect(() => {
        setColumnOrder(
            columnOrdering
                .filter((k) => selectedColumns.includes(k))
                .map((k) => columnsConfig[k]?.title)
        );
    }, [selectedColumns, columnOrdering, columnsConfig]);

    // --- data fetch ---
    const buildParams = useCallback(
        (overrides = {}) => ({
            page: pageNumber,
            pageSize: PAGE_SIZE,
            serviceType: "PACK_REPLACEMENT",
            ...(sorting?.sortBy && { sortBy: SORT_KEY_MAP[sorting.sortBy] || sorting.sortBy, order: sorting.orderBy }),
            ...(searchTerm?.length && {
                search: searchTerm.join(","),
                searchBy: searchField,
            }),
            ...mapSelectedFiltersToParams(selectedFilters),
            ...overrides,
        }),
        [pageNumber, sorting, searchTerm, searchField, selectedFilters]
    );

    const fetchData = useCallback(async () => {
        if (!config) return;
        setIsLoading(true);
        try {
            const res = await getBatteryServiceRecords(batteryID, buildParams());
            const records = res?.response?.data || [];
            const rows = groupServiceRecordRows(records, config);
            setTableData(rows);
            // A search term can match a pack serial, and packs are child rows, so a
            // collapsed result would hide the very thing that matched. Every row the
            // API returned is a match, so open them all. A plain listing stays
            // collapsed.
            setExpandedRowKeys(searchTerm?.length ? getExpandableRowKeys(rows) : []);
            setTotal(res?.response?.total?.total ?? records.length);
            // Accumulate distinct recorders ({userID, name}) for the "Recorded by"
            // filter — the option id is the userID (what the backend filters on),
            // the name is only for display. Return the previous array unchanged
            // when nothing new appears so the buildFilters effect doesn't re-run.
            setRecorders((prev) => {
                const byId = new Map(prev.map((r) => [r.userID, r]));
                let changed = false;
                records.forEach((r) => {
                    const userID = r?.serviceDetails?.recordedByUserID;
                    const name = r?.serviceDetails?.recordedByName;
                    if (userID != null && !byId.has(userID)) {
                        byId.set(userID, { userID, name: name || String(userID) });
                        changed = true;
                    }
                });
                return changed ? Array.from(byId.values()) : prev;
            });
        } catch (err) {
            console.error("Failed to load replacement logs:", err);
            setTableData([]);
            setTotal(0);
        } finally {
            setIsLoading(false);
        }
    }, [batteryID, config, buildParams, searchTerm]);

    useEffect(() => {
        if (!config) return;
        const key = JSON.stringify({ pageNumber, sorting, searchTerm, searchField, selectedFilters });
        if (key !== lastFetchParams.current) {
            lastFetchParams.current = key;
            fetchData();
        }
    }, [config, pageNumber, sorting, searchTerm, searchField, selectedFilters, fetchData]);

    // Reset to page 1 when filters/search/sort change.
    useEffect(() => {
        setPageNumber(1);
    }, [selectedFilters, searchTerm, searchField, sorting]);

    // --- export ---
    // Export honours the active filters + search. When rows are explicitly
    // selected, restrict to those record ids; otherwise export ALL matching rows.
    const exportBody = (actionType, extra = {}) => ({
        actionType,
        ...mapSelectedFiltersToParams(selectedFilters),
        ...(searchTerm?.length && { search: searchTerm.join(","), searchBy: searchField }),
        ...(selectedRowKeys?.length && { serviceRecordIDs: selectedRowKeys }),
        columnOrder,
        columns: JSON.stringify(tableColumns.filter((c) => c.dataIndex !== "actions")),
        ...extra,
    });

    // No transform is mirrored here on purpose. The repo's Data Transform Parity
    // Rule requires the download to show exactly what the table shows, and for this
    // table the backend produces those values itself: _buildServiceRecordExportRows
    // resolves observation and warranty codes through the same orgMeta lists, applies
    // the same pack -> record warranty fallback, derives the same pack count, and
    // emits one row per pack. Its keys are the frontend dataIndexes, because
    // convertDataForCSV silently drops any it does not recognise.
    //
    // Parity is pinned by tests/serviceRecord/unit/test_export_rows_pure.py. Adding a
    // display transform to a column here means adding it there too.
    const getCSVData = async () => {
        const res = await serviceRecordsListAction(batteryID, exportBody("download-csv"));
        return res?.response?.data ?? res?.response ?? [];
    };

    const onSendEmail = async ({ sendTo, sendCc, subject, body }) => {
        await serviceRecordsListAction(
            batteryID,
            exportBody("mail", { emailTo: sendTo, emailCc: sendCc, subject, body })
        );
        openNotification({ text: "Email sent", icon: "toast-success" });
    };

    const applySelectedColumns = (newKeys) => setSelectedColumns(newKeys);

    const onExpand = (expanded, record) =>
        setExpandedRowKeys((prev) =>
            expanded ? [...prev, record.id] : prev.filter((k) => k !== record.id)
        );

    // Keep CSV column order in sync when the user drag-reorders columns.
    const onColumnOrderChange = (cols) =>
        setColumnOrder(
            (cols || [])
                .filter((c) => c.dataIndex !== "actions")
                .map((c) => c.headerLabel || c.title)
        );

    return (
        <Box
            sx={{
                mt: "16px",
                // The expand control is appended into the first cell, ahead of the
                // cell's own content. Left alone the content sits on the next line,
                // because it renders as a block.
                //
                // The cell itself must stay a table-cell: making it a flex container
                // takes it out of the row box, and the row's other cells -- the
                // selection checkbox among them -- stop sharing its height and
                // visibly drift out of line.
                "& .ant-table-cell-with-append": {
                    whiteSpace: "nowrap",
                },
                "& .ant-table-cell-with-append > *": {
                    display: "inline-block",
                    verticalAlign: "middle",
                },
                // A pack row reads as belonging to the request above it. The
                // horizontal indent is AntD's own (ant-table-row-indent), so only
                // the tint is added here.
                "& .ant-table-row-level-1 > td": {
                    backgroundColor: "#FAFAFC",
                },
            }}
        >
            <GenericDraggableTableView
                title="Replacement Logs"
                count={total}
                showPrimaryButton={false}
                applySearch={(terms, field) => {
                    setSearchTerm(terms || []);
                    setSearchField(field || DEFAULT_SEARCH_FIELD);
                }}
                searchableFields={SEARCH_FIELDS}
                searchSuggestionsHandler={(search, columnName, limit) =>
                    getServiceRecordSearchSuggestions(
                        batteryID,
                        search,
                        limit,
                        columnName || DEFAULT_SEARCH_FIELD
                    )
                }
                availableEmailDomains={emailDomains}
                filters={filters}
                selectedFilters={selectedFilters}
                applyFilters={setSelectedFilters}
                initialColumns={allColumns}
                selectedColumns={selectedColumns}
                applySelectedColumns={applySelectedColumns}
                defaultSelectedColumns={defaultSelected}
                maxColumnsAllowed={LOGS_MAX_COLUMNS}
                tableColumns={tableColumns}
                tableData={tableData}
                onColumnOrderChange={onColumnOrderChange}
                columnOrder={columnOrder}
                isLoading={isLoading}
                pageNumber={pageNumber}
                rowsPerPage={PAGE_SIZE}
                total={total}
                applySort={(key, order) => setSorting({ sortBy: key, orderBy: order })}
                onPageChange={(page) => setPageNumber(page)}
                initialSorting={sorting || {}}
                getTotalRowSelection={() => selectedRowKeys}
                getTotalRowSelectionCount={() => selectedRowKeys.length}
                getCSVData={getCSVData}
                onSendEmail={onSendEmail}
                emptyMessage="No replacement logs found"
                expandable={{
                    expandedRowKeys,
                    onExpand,
                    expandIcon: LogsExpandIcon,
                }}
                rowSelection={{
                    selectedRowKeys,
                    // Selection stays at request level: the export expands each
                    // selected request to its packs, so a pack row has nothing of
                    // its own to select. Hidden rather than disabled, to avoid a
                    // greyed checkbox on every child of every open request.
                    getCheckboxProps: (record) =>
                        record._isPackRow
                            ? { disabled: true, style: { visibility: "hidden" } }
                            : {},
                    onSelect: (record, selected) => {
                        // Guarded as well as hidden: a pack row carries no id, and
                        // relying only on getCheckboxProps would put undefined into
                        // the export the moment that prop moved or was reworked.
                        if (record._isPackRow) return;
                        setSelectedRowKeys((prev) =>
                            selected ? [...prev, record.id] : prev.filter((k) => k !== record.id)
                        );
                    },
                    // Pack rows are filtered out: they carry no `id`, so including
                    // them would push undefined into the selected keys and then into
                    // the export's serviceRecordIDs.
                    onSelectAll: (selected, rows) =>
                        setSelectedRowKeys(
                            selected
                                ? (rows || []).filter((r) => !r._isPackRow).map((r) => r.id)
                                : []
                        ),
                }}
                setSelectedRowKeys={() => setSelectedRowKeys([])}
                onClearSelection={() => setSelectedRowKeys([])}
            />
        </Box>
    );
}
